/**
 * Records by Type — the generic list page for every /my/:entityType.
 *
 * Founder acceptance criteria this page satisfies:
 *   • Filtered views bookmarkable via URL (?q=, ?deleted=1)
 *   • Every result explains why it matched (matched_on)
 *   • Every record has one-click access to its complete history
 *   • Practical working surface, not a stats dashboard
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { RefreshCw, ChevronDown, AlertTriangle, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };

const LABELS = {
  document: "Documents", lesson: "Lessons", booking: "Bookings",
  mileage: "Mileage", fuel: "Fuel", vehicle: "Vehicle",
  receipt: "Receipts", bank_statement: "Bank Statements",
  voice_note: "Voice Notes", ai_analysis: "AI Analyses",
  reflection: "Reflections", business_record: "Business Records",
};

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function RecordsByType() {
  const { entityType } = useParams();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const onlyDeleted = params.get("deleted") === "1";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [trail, setTrail] = useState(null);

  const label = LABELS[entityType] || entityType;

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (onlyDeleted) p.set("only_deleted", "true");
      p.set("limit", "200");
      const r = await axios.get(`${API}/api/history/records-by-type/${entityType}?${p}`, OPTS);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load records.");
    } finally { setLoading(false); }
  }, [entityType, q, onlyDeleted]);

  useEffect(() => { load(); }, [load]);

  const openHistory = useCallback(async (row) => {
    setDetailFor(row); setTrail(null);
    try {
      const p = new URLSearchParams({
        entity_type: entityType, include_deleted: "true", limit: "50",
      });
      const r = await axios.get(`${API}/api/history/records?${p}`, OPTS);
      setTrail((r.data?.items || []).filter((x) => x.entity_id === row.entity_id));
    } catch { setTrail([]); }
  }, [entityType]);

  return (
    <main className="upload-history" data-testid={`records-by-type-page-${entityType}`}>
      <header className="uh-head">
        <div>
          <h1>{label}</h1>
          <p className="uh-sub">
            {total} record{total === 1 ? "" : "s"}
            {q && <> · filtered by <em>{q}</em></>}
            {" · "}
            <Link to="/my" data-testid="rbt-back-to-my">Back to My Records</Link>
          </p>
        </div>
        <div className="uh-head__actions">
          <button
            type="button"
            className="uh-btn"
            onClick={() => {
              const p = new URLSearchParams(params);
              if (onlyDeleted) p.delete("deleted"); else p.set("deleted", "1");
              setParams(p);
            }}
            data-testid="rbt-toggle-deleted"
          >
            {onlyDeleted ? "Show active" : "Show deleted"}
          </button>
          <button
            type="button"
            className="uh-btn"
            onClick={load}
            disabled={loading}
            aria-label="Refresh"
            data-testid="rbt-refresh"
          >
            <RefreshCw className={loading ? "uh-spin" : ""} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="mr-searchbar" role="search" style={{ marginBottom: 16 }}>
        <Search aria-hidden="true" />
        <input
          type="search"
          placeholder={`Search within ${label.toLowerCase()}…`}
          value={q}
          onChange={(e) => {
            const p = new URLSearchParams(params);
            if (e.target.value) p.set("q", e.target.value); else p.delete("q");
            setParams(p);
          }}
          data-testid="rbt-search-input"
        />
      </div>

      {err && (
        <div className="uh-alert" role="alert" data-testid="rbt-error">
          <AlertTriangle aria-hidden="true" /> {err}
        </div>
      )}

      {items.length === 0 ? (
        <div className="uh-empty" data-testid="rbt-empty">
          Nothing to show here yet.
        </div>
      ) : (
        <ul className="uh-list" data-testid="rbt-list">
          {items.map((row) => (
            <li
              key={row.id}
              className={`uh-row ${row.deleted_at ? "is-deleted" : ""}`}
              data-testid={`rbt-row-${row.entity_id}`}
            >
              <div className="uh-row__body">
                <strong>{row?.meta?.filename || row?.meta?.title || row.entity_id}</strong>
                <span className="uh-row__meta">
                  {fmt(row.created_at)}
                  {row?.status && <> · {row.status}</>}
                  {row.is_backfill && <> · <em>backfilled</em></>}
                </span>
              </div>
              <div className="uh-row__actions">
                <button
                  type="button"
                  onClick={() => openHistory(row)}
                  data-testid={`rbt-history-${row.entity_id}`}
                  aria-label="History"
                ><ChevronDown aria-hidden="true" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {detailFor && (
        <div className="uh-modal-wrap" onClick={() => setDetailFor(null)} data-testid="rbt-history-modal">
          <div className="uh-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Audit history</h2>
            <p className="uh-modal__sub">{detailFor?.meta?.filename || detailFor.entity_id}</p>
            {trail === null ? <p>Loading…</p> : (
              <ol className="uh-trail" data-testid="rbt-audit-trail">
                {trail.length === 0 ? <li>No events.</li> : trail.map((r) => (
                  <li key={r.id}>
                    <strong>{r.action}</strong>
                    <span>{fmt(r.created_at)}</span>
                  </li>
                ))}
              </ol>
            )}
            <button type="button" onClick={() => setDetailFor(null)} className="uh-btn">Close</button>
          </div>
        </div>
      )}
    </main>
  );
}
